import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../redux/store";
import {
  Typography,
  Container,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
} from "@mui/material";
import useTabTitle from "../hooks/useTabTitle.hook";
import useDetectSession from "../hooks/useDetectSession.hook";
import AppContainer from "../components/AppContainer";
import { pageHeading } from "../styles/styling";
import { listAuditTrail } from "../redux/slices/auditTrailSlice";
import { format } from "date-fns";
import LoadingOverlay from "../components/LoadingOverlay";

const AuditTrailPage: React.FC = () => {
  useTabTitle();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const idToken = localStorage.getItem("idToken");
  useDetectSession();

  const { auditTrail, status, error } = useSelector(
    (state: RootState) => state.auditTrail
  );

  const [rowsPerPage, setRowsPerPage] = useState<number>(15);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const rowsPerPageArr = [10, 15, 25, 50, 75, 100];

  const columns = [
    { field: "timestamp", headerName: "Timestamp", width: "80px" },
    { field: "userInfo", headerName: "Modified by", width: "130px" },
    { field: "actionType", headerName: "Action Type", width: "80px" },
    {
      field: "objectAffected",
      headerName: "Object Affected",
      width: "130px",
    },
  ];

  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setCurrentPage(0);
  };

  useEffect(() => {
    if (!idToken) {
      navigate("/login");
      return;
    }

    dispatch(listAuditTrail({ rowsPerPage: rowsPerPage, page: currentPage }));
  }, [currentPage, dispatch, idToken, navigate, rowsPerPage]);

  if (error) {
    return (
      <AppContainer>
        <Container sx={{ marginLeft: "240px", flexGrow: 1, padding: 2 }}>
          <Typography variant="h6" color="error">
            Error: {error}
          </Typography>
        </Container>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Box sx={{ flexGrow: 1, padding: 2 }}>
        <LoadingOverlay loading={status === "loading"} />
        <Box display="flex" alignItems="center" marginBottom={2}>
          <Typography variant="h4" sx={pageHeading}>
            Audit Trail
          </Typography>
        </Box>

        {auditTrail.totalItems === 0 ? (
          <Typography>No available records.</Typography>
        ) : (
          <Paper sx={{ width: "100%" }}>
            <TableContainer>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={"left"}
                        style={{ width: column.width, fontWeight: "bold" }}
                      >
                        {column.headerName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditTrail.data.map((row: any) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id}
                      >
                        {columns.map((column: any) => {
                          const value = row[column.field];
                          return (
                            <TableCell
                              key={`${row.id} - ${column.field}`}
                              align={"left"}
                            >
                              {column.field === "timestamp"
                                ? format(value, "MM/dd/yyyy kk:mm:ss")
                                : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageArr}
              component="div"
              count={auditTrail.totalItems}
              rowsPerPage={rowsPerPage}
              page={currentPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </Box>
    </AppContainer>
  );
};

export default AuditTrailPage;
